import { useQuery, useMutation } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"
import { queryClient } from "./QueryClientProvider"

interface ErrorResponse {
  message?: string
}

/** ------------------- **/
/*  Groups
/** ------------------- **/

export function useAddCompany() {
  return useMutation(
    ({ groupId, companyToAdd }: { groupId: string; companyToAdd: string }) => {
      return apiRequest(`group/add_company`, "POST", {
        group_id: groupId,
        company_to_add: companyToAdd,
      })
    },
    {
      onSuccess: () => {
        toast.success("Company has been added successfully")
        queryClient.invalidateQueries(["user"])
      },
      onError: (error: ErrorResponse) => {
        if (error.message) {
          toast.error(error.message)
        } else {
          toast.error("There was a problem adding that company")
        }
      },
    }
  )
}

export function useRemoveCompany() {
  return useMutation(
    ({ groupId, companyId }: { groupId: string; companyId: string }) => {
      const paramsString = qs.stringify({
        group_id: groupId,
        company_id: companyId,
      })
      return apiRequest(`group/remove_company?${paramsString}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("Company was removed successfully")
        queryClient.invalidateQueries(["user"])
      },
      onError: (error: ErrorResponse) => {
        console.error(error)
        if (error.message) {
          toast.error(error.message)
        } else {
          toast.error("Hmm, there was an issue removing that company")
        }
      },
    }
  )
}

export function useInviteUser() {
  return useMutation(
    ({
      email,
      role,
      groupId,
      isResend,
    }: {
      email: string
      role: string
      groupId: string
      isResend?: boolean
    }) => {
      return apiRequest(`user`, "POST", {
        email,
        type: role,
        group_id: groupId,
        is_resend: isResend || false,
      })
    },
    {
      onSuccess: () => {
        toast.success("User has been invited")
      },
      onError: (error: ErrorResponse) => {
        console.error(error.message)
        if (error.message?.includes("UsernameExistsException")) {
          toast.error("Hmm, looks like that user has already been invited")
        } else {
          toast.error("Hmm, there was an issue inviting that user")
        }
      },
    }
  )
}

export function useDeleteUser() {
  return useMutation(
    ({ userId, groupId }: { userId: string; groupId: string }) => {
      const paramsString = qs.stringify({
        user_id: userId,
        group_id: groupId,
      })
      return apiRequest(`user?${paramsString}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("User deleted successfully")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue deleting that user")
      },
    }
  )
}

export function useGetHistoricalUploadUrl({
  groupId,
  enabled = false,
}: {
  groupId?: string
  enabled?: boolean
}) {
  const cacheKey = ["historicalSignedUrl", groupId]
  const query = () => apiRequest(`group/${groupId}/upload_url`)
  return useQuery(cacheKey, query, { enabled: !!groupId && enabled })
}

export function useGetDemandPlanning({ groupId }: { groupId?: string }) {
  const cacheKey = ["demandPlanning", groupId]
  const query = () => apiRequest(`group/${groupId}/demand_planning`)
  return useQuery(cacheKey, query, {
    enabled: !!groupId,
  })
}
